/**
 * DealStagesManager Component
 * 
 * Admin component to manage CRM deal stages stored in Supabase.
 * Supports drag-and-drop reordering.
 * 
 * Architecture:
 * - Stages are stored in Supabase crm_deal_stages table
 * - Real-time updates via CrmSettingsContext subscription
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select as ChakraSelect,
  useToast,
  useColorModeValue,
  Center,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Switch,
  Tooltip,
  Checkbox,
  Icon,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiMenu,
  FiEye,
  FiEyeOff,
} from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { ReactSortable } from 'react-sortablejs';

const AVAILABLE_COLORS = [
  { value: 'gray', label: 'Gray' },
  { value: 'red', label: 'Red' },
  { value: 'orange', label: 'Orange' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'green', label: 'Green' },
  { value: 'teal', label: 'Teal' },
  { value: 'blue', label: 'Blue' },
  { value: 'cyan', label: 'Cyan' },
  { value: 'purple', label: 'Purple' },
  { value: 'pink', label: 'Pink' },
];

/**
 * DealStagesManager - Admin component for managing deal stages in Supabase
 */
export default function DealStagesManager() {
  const toast = useToast();
  const { dealStages, stagesLoading, refreshStages } = useCrmSettings();
  
  const [selectedStage, setSelectedStage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localStages, setLocalStages] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');
  const dragHandleBG = useColorModeValue('gray.100', 'gray.600');

  // Sync local stages with context when dealStages changes
  useEffect(() => {
    if (dealStages && dealStages.length > 0) {
      // Use the stage's id for sortablejs, or fall back to value
      setLocalStages(dealStages.map(s => ({ 
        ...s, 
        id: s.id || s.value 
      })));
      setOrderChanged(false);
    }
  }, [dealStages]);

  // Check if Supabase is configured (after all hooks)
  if (!isSupabaseConfigured()) {
    return (
      <Alert status="warning">
        <AlertIcon />
        Supabase is not configured. Deal stages cannot be managed.
      </Alert>
    );
  }

  const handleAdd = () => {
    setSelectedStage(null);
    onOpen();
  };

  const handleEdit = (stage) => {
    setSelectedStage(stage);
    onOpen();
  };

  const handleDelete = async (stage) => {
    if (!window.confirm(`Are you sure you want to delete the stage "${stage.label}"? This may affect existing deals.`)) {
      return;
    }

    setSaving(true);
    try {
      // Soft delete by setting is_active = false
      const { error } = await supabase
        .from('crm_deal_stages')
        .update({ is_active: false })
        .eq('value', stage.value);

      if (error) throw error;

      toast({
        title: 'Stage deleted',
        status: 'success',
        duration: 2000,
      });

      // Refresh stages from context
      refreshStages();
    } catch (error) {
      console.error('Error deleting stage:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Toggle hide/unhide. Unlike Delete (is_active=false), this keeps the stage
  // active so badges/colors still render and Arive can still set it — it only
  // removes the stage from manual selection menus.
  const handleToggleHidden = async (stage) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('crm_deal_stages')
        .update({ is_hidden: !stage.isHidden })
        .eq('value', stage.value);

      if (error) throw error;

      toast({
        title: stage.isHidden ? 'Stage shown' : 'Stage hidden',
        description: stage.isHidden
          ? `"${stage.label}" will appear in selection menus`
          : `"${stage.label}" is hidden from selection menus (still valid for existing deals & Arive sync)`,
        status: 'success',
        duration: 2500,
      });

      refreshStages();
    } catch (error) {
      console.error('Error toggling stage visibility:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle reorder via drag-and-drop
  const handleReorder = (newList) => {
    setLocalStages(newList);
    // Check if order actually changed
    const originalOrder = dealStages.map(s => s.value).join(',');
    const newOrder = newList.map(s => s.value).join(',');
    setOrderChanged(originalOrder !== newOrder);
  };

  // Save new order to Supabase
  const saveOrder = async () => {
    setSaving(true);
    try {
      // Update sort_order for each stage
      const updates = localStages.map((stage, index) => ({
        value: stage.value,
        sort_order: index + 1,
      }));

      // Update each stage's sort_order
      for (const update of updates) {
        const { error } = await supabase
          .from('crm_deal_stages')
          .update({ sort_order: update.sort_order })
          .eq('value', update.value);

        if (error) throw error;
      }

      toast({
        title: 'Order saved',
        description: 'Stage order has been updated',
        status: 'success',
        duration: 2000,
      });
      
      setOrderChanged(false);
      refreshStages();
    } catch (error) {
      console.error('Error saving order:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Deal Stages</Heading>
            <Text fontSize="sm" color="gray.600">
              Manage the stages/statuses available for deals
            </Text>
          </VStack>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            size="sm"
            onClick={handleAdd}
          >
            Add Stage
          </Button>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Drag stages to reorder them. Toggle <strong>Shown in Menus</strong> to hide a stage
            from selection menus (DealPage, Quick Stage Change, Bulk Edit) while keeping it valid
            for existing deals and Arive sync. <strong>Delete</strong> removes a stage everywhere.
          </Text>
        </Alert>

        {orderChanged && (
          <Flex justify="flex-end">
            <Button 
              colorScheme="green" 
              size="sm" 
              onClick={saveOrder}
              isLoading={saving}
              loadingText="Saving..."
            >
              Save Order
            </Button>
          </Flex>
        )}

        {stagesLoading ? (
          <Center py={8}>
            <Spinner />
          </Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg={tableHeaderBG}>
                <Tr>
                  <Th w="40px"></Th>
                  <Th>Preview</Th>
                  <Th>Value (ID)</Th>
                  <Th>Label</Th>
                  <Th>Color</Th>
                  <Th>Shown in Menus</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <ReactSortable
                tag={Tbody}
                list={localStages}
                setList={handleReorder}
                animation={200}
                handle=".drag-handle"
              >
                {localStages.map((stage) => (
                  <Tr key={stage.value} _hover={{ bg: hoverBG }}>
                    <Td p={1}>
                      <IconButton
                        icon={<FiMenu />}
                        size="xs"
                        variant="ghost"
                        aria-label="Drag to reorder"
                        className="drag-handle"
                        cursor="grab"
                        _hover={{ bg: dragHandleBG }}
                      />
                    </Td>
                    <Td opacity={stage.isHidden ? 0.55 : 1}>
                      <HStack spacing={2}>
                        <Badge colorScheme={stage.color}>{stage.label}</Badge>
                        {stage.isHidden && (
                          <Badge colorScheme="gray" variant="outline">Hidden</Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td fontFamily="mono" fontSize="xs" opacity={stage.isHidden ? 0.55 : 1}>{stage.value}</Td>
                    <Td fontWeight="medium" opacity={stage.isHidden ? 0.55 : 1}>{stage.label}</Td>
                    <Td opacity={stage.isHidden ? 0.55 : 1}>
                      <Badge colorScheme={stage.color} variant="solid">
                        {stage.color}
                      </Badge>
                    </Td>
                    <Td>
                      <Tooltip
                        label={
                          stage.isHidden
                            ? 'Hidden from selection menus. Toggle on to show it again.'
                            : 'Shown in selection menus. Toggle off to hide it (stays valid for existing deals & Arive sync).'
                        }
                      >
                        <HStack spacing={2}>
                          <Switch
                            size="sm"
                            colorScheme="blue"
                            isChecked={!stage.isHidden}
                            onChange={() => handleToggleHidden(stage)}
                            isDisabled={saving}
                            aria-label={stage.isHidden ? 'Show stage in menus' : 'Hide stage from menus'}
                          />
                          <Icon
                            as={stage.isHidden ? FiEyeOff : FiEye}
                            color={stage.isHidden ? 'gray.400' : 'green.500'}
                          />
                        </HStack>
                      </Tooltip>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton
                          icon={<FiEdit2 />}
                          size="xs"
                          variant="ghost"
                          aria-label="Edit"
                          onClick={() => handleEdit(stage)}
                        />
                        <Tooltip label="Delete removes this stage everywhere. To just take it off the menus, use the Shown in Menus toggle instead.">
                          <IconButton
                            icon={<FiTrash2 />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            aria-label="Delete"
                            onClick={() => handleDelete(stage)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </ReactSortable>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Editor Modal */}
      <EditorModal
        isOpen={isOpen}
        onClose={onClose}
        stage={selectedStage}
        existingStages={dealStages || []}
        onSave={refreshStages}
      />
    </Box>
  );
}

function EditorModal({ isOpen, onClose, stage, existingStages, onSave }) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('blue');
  const [isHidden, setIsHidden] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (stage) {
      setValue(stage.value || '');
      setLabel(stage.label || '');
      setColor(stage.color || 'blue');
      setIsHidden(stage.isHidden || false);
    } else {
      setValue('');
      setLabel('');
      setColor('blue');
      setIsHidden(false);
    }
  }, [stage, isOpen]);

  const handleSave = async () => {
    if (!value.trim() || !label.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Value and label are required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Validate value format (lowercase, hyphenated)
    if (!/^[a-z0-9-]+$/.test(value)) {
      toast({
        title: 'Invalid value format',
        description: 'Value must be lowercase alphanumeric with hyphens only',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      if (stage) {
        // Update existing stage
        const { error } = await supabase
          .from('crm_deal_stages')
          .update({
            label: label.trim(),
            color,
            is_hidden: isHidden,
          })
          .eq('value', stage.value);

        if (error) throw error;

        toast({
          title: 'Stage updated',
          description: 'Deal stage updated successfully',
          status: 'success',
          duration: 2000,
        });
      } else {
        // Check for duplicate value
        if (existingStages.some(s => s.value === value.trim())) {
          toast({
            title: 'Duplicate value',
            description: 'A stage with this value already exists',
            status: 'error',
            duration: 3000,
          });
          setIsSaving(false);
          return;
        }

        // Calculate sort_order (add to end)
        const maxSortOrder = Math.max(0, ...existingStages.map(s => s.sortOrder || 0));

        // Insert new stage
        const { error } = await supabase
          .from('crm_deal_stages')
          .insert({
            value: value.trim(),
            label: label.trim(),
            color,
            sort_order: maxSortOrder + 1,
            is_active: true,
            is_hidden: isHidden,
          });

        if (error) throw error;

        toast({
          title: 'Stage created',
          description: 'Deal stage created successfully',
          status: 'success',
          duration: 2000,
        });
      }

      // Refresh stages
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving stage:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {stage ? 'Edit' : 'Create'} Deal Stage
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Value (Unique ID)</FormLabel>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., loan-qualification"
                isDisabled={!!stage} // Can't change value after creation
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Lowercase, alphanumeric, hyphens only. Cannot be changed after creation.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Display Label</FormLabel>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Qualified"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Color</FormLabel>
              <ChakraSelect
                value={color}
                onChange={(e) => setColor(e.target.value)}
              >
                {AVAILABLE_COLORS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </ChakraSelect>
              <HStack spacing={2} mt={2}>
                <Text fontSize="xs">Preview:</Text>
                <Badge colorScheme={color}>{label || 'Sample'}</Badge>
              </HStack>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
              >
                Hidden from selection menus
              </Checkbox>
              <Text fontSize="xs" color="gray.600" mt={1}>
                The stage stays valid (badges still render, Arive sync still works) but won&apos;t
                appear in the DealPage, Quick Stage Change, or Bulk Edit menus.
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            {stage ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
